import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { CreditCard, RefreshCw, TrendingUp, TrendingDown, Clock, DollarSign, AlertCircle } from 'lucide-react';
import { creditsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

interface CreditTransaction {
  id: string;
  type: string;
  amount: number;
  balance_after: number;
  reference_type: string;
  reference_id: string;
  description: string;
  created_at: string;
}

interface CreditBalance {
  available_credits: number;
  total_purchased: number;
  total_consumed: number;
  recent_transactions: CreditTransaction[];
}

interface CreditBalanceProps {
  onRefresh?: () => void;
  showTransactions?: boolean;
  compact?: boolean;
}

const CreditBalance: React.FC<CreditBalanceProps> = ({ 
  onRefresh, 
  showTransactions = false, 
  compact = false 
}) => {
  const { user, loading: authLoading } = useAuth();
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCreditBalance = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await creditsAPI.getBalance();
      setBalance(response);
    } catch (err) {
      setError('Failed to fetch credit balance');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Wait for authentication to complete before making API calls
    if (authLoading || !user) {
      return;
    }
    
    fetchCreditBalance();
  }, [authLoading, user, fetchCreditBalance]);

  // Memoize transaction icons for better performance
  const getTransactionIcon = useCallback((transaction: CreditTransaction) => {
    if (transaction.type === 'consumption' && (transaction.reference_type === 'sms' || transaction.reference_type === 'email')) {
      return <TrendingDown className="w-4 h-4 text-orange-500" />;
    }
    switch (transaction.type) {
      case 'purchase':
        return <TrendingUp className="w-4 h-4 text-green-500" />;
      case 'consumption':
        return <TrendingDown className="w-4 h-4 text-red-500" />;
      case 'refund':
        return <RefreshCw className="w-4 h-4 text-blue-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  }, []);

  // Memoize formatted balance for performance
  const formattedBalance = useMemo(() => {
    if (!balance) return { available: '0', purchased: '0', consumed: '0' };
    return {
      available: balance.available_credits.toLocaleString(),
      purchased: balance.total_purchased.toLocaleString(),
      consumed: balance.total_consumed.toLocaleString()
    };
  }, [balance]);

  const handleRefresh = () => {
    fetchCreditBalance();
    onRefresh?.();
  };

  const formatTransactionType = (transaction: CreditTransaction) => {
    switch (transaction.type) {
      case 'purchase': return 'Credit Purchase';
      case 'consumption':
        if (transaction.reference_type === 'sms') return 'SMS Sent';
        if (transaction.reference_type === 'email') return 'Email sent';
        return 'Call Made';
      case 'refund': return 'Refund';
      case 'adjustment': return 'Adjustment';
      default: return transaction.type;
    }
  };

  const formatTransactionAmount = (amount: number) => {
    return amount > 0 ? `+${amount}` : `${amount}`;
  };


  if (loading) {
    return (
      <div className="group relative overflow-hidden bg-white/10 backdrop-blur-md rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-white/10">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-600 opacity-0 group-hover:opacity-5 transition-opacity duration-300"></div>
        <div className="relative p-2 sm:p-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-1 sm:space-x-2 mb-1 sm:mb-2">
                <div className="p-2 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg shadow-lg">
                  <CreditCard className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider">Credits</h3>
              </div>
              <div className="h-6 bg-gray-600 rounded w-16 mb-1"></div>
              <div className="h-4 bg-gray-600 rounded w-20"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !balance) {
    return (
      <div className="bg-white/10 backdrop-blur-md p-4 rounded-lg shadow border border-red-400/50">
        <div className="flex items-center">
          <div className="text-red-400 mr-2">⚠️</div>
          <div>
            <p className="text-red-300 font-medium">Error loading credits</p>
            <p className="text-red-400 text-sm">{error}</p>
            <button 
              onClick={handleRefresh}
              className="mt-2 text-red-400 hover:text-red-300 underline text-sm"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="group relative overflow-hidden bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl rounded-xl shadow-2xl hover:shadow-cyan-500/20 transition-all duration-300 transform hover:-translate-y-1 border border-slate-700/50">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 to-blue-600/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        <div className="relative p-2 sm:p-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-1 sm:space-x-2 mb-1 sm:mb-2">
                <div className="p-1 sm:p-2 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg shadow-lg">
                  <CreditCard className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
                </div>
                <h3 className="text-xs font-semibold text-slate-300 uppercase tracking-wider">Credits</h3>
              </div>
              <p className="text-sm sm:text-lg font-bold text-white mb-1">
                {formattedBalance.available}
              </p>
              <div className="flex items-center text-xs text-slate-400">
                <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-cyan-400 rounded-full mr-1"></div>
                {formattedBalance.consumed} used
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white/10 backdrop-blur-md p-6 rounded-lg shadow border border-white/10">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-white flex items-center">
          <CreditCard className="h-5 w-5 mr-2 text-cyan-400" />
          Credit Balance
        </h3>
        <button 
          onClick={handleRefresh}
          className="p-2 text-gray-400 hover:text-white transition-colors"
          title="Refresh balance"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="text-center p-4 bg-green-500/10 rounded-lg border border-green-500/20">
          <div className="text-2xl font-bold text-green-400">
            {formattedBalance.available}
          </div>
          <div className="text-sm text-green-300 font-medium">Available</div>
        </div>
        <div className="text-center p-4 bg-cyan-500/10 rounded-lg border border-cyan-500/20">
          <div className="text-2xl font-bold text-cyan-400">
            {formattedBalance.purchased}
          </div>
          <div className="text-sm text-cyan-300 font-medium">Purchased</div>
        </div>
        <div className="text-center p-4 bg-orange-500/10 rounded-lg border border-orange-500/20">
          <div className="text-2xl font-bold text-orange-400">
            {formattedBalance.consumed}
          </div>
          <div className="text-sm text-orange-300 font-medium">Used</div>
        </div>
      </div>

      {showTransactions && balance.recent_transactions.length > 0 && (
        <div>
          <h4 className="text-md font-medium text-white mb-3">Recent Transactions</h4>
          <div className="space-y-2">
            {balance.recent_transactions.slice(0, 5).map((transaction) => (
              <div key={transaction.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg border border-white/10">
                <div className="flex items-center">
                  {getTransactionIcon(transaction)}
                  <div className="ml-3">
                    <div className="text-sm font-medium text-white">
                      {formatTransactionType(transaction)}
                    </div>
                    <div className="text-xs text-gray-300">
                      {transaction.description}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-sm font-medium ${
                    transaction.amount > 0 ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {formatTransactionAmount(transaction.amount)}
                  </div>
                  <div className="text-xs text-gray-300">
                    {new Date(transaction.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 pt-4 border-t border-white/10">
        <button 
          onClick={() => window.location.href = '/subscription'}
          className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white py-2 px-4 rounded-md hover:from-cyan-600 hover:to-blue-700 transition-colors flex items-center justify-center"
        >
          <DollarSign className="h-4 w-4 mr-2" />
          Purchase More Credits
        </button>
      </div>
    </div>
  );
};

export default CreditBalance;
